import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ArrowsRightLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CONTROL_SLIDE_IMAGE_COUNT } from './controlSlides';

// Match vasi-driver's engine::LAYOUT_POSITIONS and evaluation::tile_size:
// 240 x 360 tiles on a 1920 x 1080 reference surface, scaled as one canvas.
const POSITIONS = [[0, 1], [0, 3], [0, 5], [1, 0], [1, 2], [1, 4], [1, 6], [2, 1], [2, 3], [2, 5]];

function shuffledSample(images) {
  const result = [...images];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const other = Math.floor(Math.random() * (index + 1));
    [result[index], result[other]] = [result[other], result[index]];
  }
  return result.slice(0, CONTROL_SLIDE_IMAGE_COUNT);
}

export default function ControlSlidePreview({ slide, onClose }) {
  const [images, setImages] = useState(() => slide.images.slice(0, CONTROL_SLIDE_IMAGE_COUNT));
  const [failedUrls, setFailedUrls] = useState({});
  const unavailable = images.filter((image) => !image.imageUrl || failedUrls[image.imageUrl]).length;
  const incomplete = slide.images.length < CONTROL_SLIDE_IMAGE_COUNT;

  return (
    <Transition.Root show appear as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity duration-150 motion-reduce:transition-none"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-100 motion-reduce:transition-none"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/70" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-3 sm:p-6">
            <Dialog.Panel className="relative w-full max-w-[100rem] overflow-hidden rounded-lg bg-white shadow-xl">
              <header className="flex items-start justify-between gap-4 px-4 py-4 sm:px-6">
                <div>
                  <Dialog.Title as="h2" className="text-lg font-semibold text-gray-900">
                    Preview: Control {slide.identifier}
                  </Dialog.Title>
                  <Dialog.Description className="mt-1 text-sm text-gray-600">
                    {[slide.description, slide.organizationName].filter(Boolean).join(' · ')}
                  </Dialog.Description>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close preview"
                  className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </header>

              {(incomplete || unavailable > 0) && (
                <div role="status" className="border-t border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:px-6">
                  {incomplete && <p>This set has {slide.images.length} of 10 images. An evaluation requires a complete set.</p>}
                  {unavailable > 0 && <p>{unavailable} {unavailable === 1 ? 'image could' : 'images could'} not be loaded. Close the preview and refresh the library to try again.</p>}
                </div>
              )}

              <div
                role="group"
                aria-label="Control slide, scaled from a 1920 by 1080 screen"
                className="relative w-full overflow-hidden"
                style={{ aspectRatio: '1920 / 1080', backgroundColor: '#1a1a2e' }}
              >
                {POSITIONS.map(([row, column], index) => {
                  const image = images[index];
                  const available = image?.imageUrl && !failedUrls[image.imageUrl];
                  return (
                    <div
                      key={index}
                      data-testid="control-preview-slot"
                      className="absolute"
                      style={{
                        left: `${column / 7 * 100}%`,
                        top: `${row / 3 * 100}%`,
                        width: `${240 / 1920 * 100}%`,
                        height: `${360 / 1080 * 100}%`,
                      }}
                    >
                      {available ? (
                        <img
                          key={image.hash || image.id || image.imageUrl}
                          src={image.imageUrl}
                          alt={`Control ${slide.identifier}, image ${image.control || index + 1}`}
                          className="h-full w-full object-contain"
                          onError={() => setFailedUrls((current) => ({ ...current, [image.imageUrl]: true }))}
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center border border-dashed border-white/30 p-1 text-center text-[10px] text-white/80 sm:text-xs">
                          {image ? 'Image unavailable' : 'Missing image'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <footer className="flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <div className="text-sm text-gray-600">
                  <p className="font-medium text-gray-900">1920 × 1080 preview · stored images</p>
                  <p className="mt-1">Positions are randomized during evaluations. This preview does not start an evaluation.</p>
                  {slide.images.length > CONTROL_SLIDE_IMAGE_COUNT && (
                    <p className="mt-1">Showing 10 of {slide.images.length} images. Shuffle to sample another arrangement.</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setImages(shuffledSample(slide.images))}
                  disabled={slide.images.length < 2}
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                >
                  <ArrowsRightLeftIcon className="h-4 w-4" aria-hidden="true" />
                  Shuffle positions
                </button>
              </footer>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
